import {
  BaseFlowModel,
  BaseFlowStepData,
} from '@kinet-open-network/client/flows/data-access';
import { BaseModel } from '@kinet-open-network/client/shared/data-access/model';
import {
  ICustomerAddress,
  OrderStatus,
  SelectionItem,
} from '@kinet-open-network/shared/data-access/model';
import {
  FlowOrderType,
  IAppOrderItem,
  IAppStockItem,
  IFlowOrder,
  IFlowOrderScannedItem,
  IFlowOrderSelectionItemData,
  IFlowOrderStepData,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { VatRateType } from '@kinet-open-network/shared/util';
import { Type } from 'class-transformer';
import { AppCustomer } from '../../../models/app-customer.model';

/**
 * Concrete flow model for the "order" flow type.
 * Extends the abstract `BaseFlowModel` base and adds order-specific fields.
 */
export class FlowOrders
  extends BaseFlowModel<FlowOrders, 'Order', string>
  implements IFlowOrder
{
  static override readonly modelName = 'Order';

  /** Current order status — drives the flow step UI. */
  status!: OrderStatus;

  type!: FlowOrderType;

  @Type(() => Number)
  customerId!: number;
  @Type(() => Date)
  date!: Date;
  note?: string | null;
  extId?: string | null;
  externalNumber?: string | null;
  paymentType?: string | null;
  @Type(() => Number)
  dueDays?: number | null;
  generateInvoice?: boolean;
  email?: string | null;
  phone?: string | null;
  contactName?: string | null;
  invoiceAddress?: ICustomerAddress | null;
  deliveryAddress?: ICustomerAddress | null;
  customer?: AppCustomer;
  @Type(() => FlowOrderItem)
  items!: FlowOrderItem[];
  @Type(() => Number)
  total!: number;
  @Type(() => Number)
  totalBase!: number;
  @Type(() => Number)
  totalVat!: number;
  stockCode?: string | null;
  @Type(() => Date)
  deletedAt?: Date | null;

  deliveryId?: string;
  @Type(() => Number)
  deliveryOrder?: number;

  @Type(() => SelectionItem)
  toCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  @Type(() => SelectionItem)
  toCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  @Type(() => SelectionItem)
  fromCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  @Type(() => SelectionItem)
  fromCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];

  unloadingStockCode?: string;

  invoiceId?: string | null;

  /** Date and time of the delivery / collection / sale. */
  @Type(() => Date)
  deliveryDate?: Date | string | null;

  @Type(() => Number)
  recipientGpsX?: number | null;
  @Type(() => Number)
  recipientGpsY?: number | null;
}

export class FlowOrderItem
  extends BaseModel<FlowOrderItem, 'Order'>
  implements IAppOrderItem
{
  static override readonly modelName = 'Order';

  orderId!: string;
  stockItemId!: string;
  stockItem?: IAppStockItem;
  @Type(() => Number)
  quantity!: number;
  @Type(() => Number)
  priceBase!: number;
  @Type(() => Number)
  price!: number;
  @Type(() => Number)
  priceVat!: number;
  vatRate!: VatRateType;
  @Type(() => Number)
  sortOrder?: number;
  @Type(() => Number)
  total!: number;
  @Type(() => Number)
  totalBase!: number;
  @Type(() => Number)
  totalVat!: number;

  isFull?: boolean | null;

  isSell?: boolean | null;

  isComplaint?: boolean | null;
}

/**
 * Determines if an item should use pricing (i.e. has isFull !== false and isComplaint !== true).
 * Used to filter items when recalculating totals or refreshing prices.
 */
export function itemShouldUsePrice(item: {
  isFull?: boolean | null;
  isComplaint?: boolean | null;
}): boolean {
  return item?.isFull !== false && item?.isComplaint !== true;
}

export class FlowOrdersStepData
  extends BaseFlowStepData<FlowOrdersStepData>
  implements IFlowOrderStepData
{
  deliveryId?: string;

  toCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  toCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  fromCustomerLoadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];
  fromCustomerUnloadedItems?: SelectionItem<
    IFlowOrderSelectionItemData,
    IFlowOrderScannedItem
  >[];

  unloadingStockCode?: string;

  isEditStep?: boolean;

  regenerateStockDocuments?: boolean;

  regenerateInvoice?: boolean;
}
